import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getProposalRevisions,
  restoreProposalRevision,
  type ProposalRevision,
} from "@/lib/proposalsApi";
import { buildProposalHistoryPreviewPath } from "@/lib/proposalPublicView";
import { ArrowLeft, RotateCcw } from "lucide-react";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const ProposalHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [restoringRevisionId, setRestoringRevisionId] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<ProposalRevision[]>([]);

  const isCeo = userRole === "ceo";

  const loadRevisions = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getProposalRevisions(id);
      setRevisions(response.items || []);
    } catch (error: any) {
      toast({
        title: "Error loading history",
        description: error?.message || "Failed to load proposal history.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRevisions();
  }, [id]);

  const handleRestoreRevision = async (revision: ProposalRevision) => {
    if (!id) return;
    const confirmed = window.confirm(
      `Restore proposal to revision v${revision.version}? This will create a new revision.`
    );
    if (!confirmed) return;

    try {
      setRestoringRevisionId(revision.id);
      await restoreProposalRevision(id, revision.id);
      toast({
        title: "Proposal restored",
        description: `Proposal restored to revision v${revision.version}.`,
      });
      await loadRevisions();
    } catch (error: any) {
      toast({
        title: "Error restoring revision",
        description: error?.message || "Failed to restore proposal.",
        variant: "destructive",
      });
    } finally {
      setRestoringRevisionId(null);
    }
  };

  if (!isCeo) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            Only CEO can access proposal history.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold">Proposal History</h1>
            <p className="text-muted-foreground">
              Full change history and restore options for proposal {id}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/proposals/${id}`)}>
              Open Proposal
            </Button>
            <Button variant="outline" onClick={() => navigate("/proposals")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Changes</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : revisions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    No history found for this proposal.
                  </TableCell>
                </TableRow>
              ) : (
                revisions.map((revision) => (
                  <TableRow key={revision.id}>
                    <TableCell className="font-medium">v{revision.version}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{revision.action}</Badge>
                    </TableCell>
                    <TableCell>{revision.actor?.actor_name || "System"}</TableCell>
                    <TableCell>{revision.actor?.actor_role || "-"}</TableCell>
                    <TableCell>{revision.changes?.length ?? 0}</TableCell>
                    <TableCell>{formatDateTime(revision.created_at)}</TableCell>
                    <TableCell>{revision.summary || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!id) return;
                            navigate(buildProposalHistoryPreviewPath(id, revision.id));
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={restoringRevisionId === revision.id}
                          onClick={() => handleRestoreRevision(revision)}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Restore
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProposalHistory;
